import {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { useCustomRouter } from 'src/shared/lib/history/hooks';
import { getSortIdQueryParam } from 'src/shared/lib/url';
import {
  HOMEPAGE_SLUG,
  LOWERCASE_SORT_ID_KEY,
  PSYCHICS_READINGS_NEW_SLUG,
} from 'constants/constants';
import type { Store } from 'app-redux/types/storeTypes';
import { PsychicCardAppearance } from 'constants/enums';
import { Logger } from 'lib/logger';
import { getFilteredPsychicsIds, getPsychicFilterOptions } from 'src/api/psychicApi';
import { usePsychicStatuses, usePsychicStatusesWithUnsubscribe } from 'src/firebase/firebase.hook';
import type { RightPsychic } from 'types/objectTypes';

import { FilterToolbarOptions, PopularFilters } from '../declarations';

import { getSortIdCookie } from '.';

export const useSortIdQueryParam = (slug: string) => {
  const router = useCustomRouter();
  const { query } = router;
  const sortingIdQueryParam = getSortIdQueryParam(query);

  useEffect(() => {
    const sortingIdCookie = getSortIdCookie();

    if ((slug === HOMEPAGE_SLUG || slug === PSYCHICS_READINGS_NEW_SLUG)
      && !sortingIdQueryParam && sortingIdCookie) {
      const cookieForQuery = sortingIdCookie.replace(/^"|"$/g, '');
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set(LOWERCASE_SORT_ID_KEY, cookieForQuery);
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      router.replace(newUrl);
    }
  }, [sortingIdQueryParam, router, slug]);

  return sortingIdQueryParam || '';
};

export const useEmbeddedItemPosition = (
  appearance: PsychicCardAppearance,
  properties?: Record<string, any>,
) => {
  const isMobile = useSelector((store: Store) => store.server.app.isMobileViewWidth);

  if (isMobile) {
    const defaultValue = 5;

    if (typeof properties?.mobile === 'number') {
      return properties.mobile + 1;
    }

    if (typeof properties?.mobile !== 'object') {
      return defaultValue;
    }

    if (appearance === PsychicCardAppearance.TILE) {
      return properties.mobile.tile + 1 || defaultValue;
    }

    if (appearance === PsychicCardAppearance.LIST) {
      return properties.mobile.list + 1 || defaultValue;
    }

    return defaultValue;
  }

  const defaultValue = 4;

  if (typeof properties?.desktop === 'number') {
    return properties.desktop + 1;
  }

  if (typeof properties?.desktop !== 'object') {
    return defaultValue;
  }

  if (appearance === PsychicCardAppearance.TILE) {
    return properties.desktop.tile + 1 || defaultValue;
  }

  if (appearance === PsychicCardAppearance.LIST) {
    return properties.desktop.list + 1 || defaultValue;
  }

  return defaultValue;
};

export const useFilteredExtIdsState = () => {
  const [filteredExtIds, setFilteredExtIds] = useState<Array<number>>([]);

  const getFilteredExtIds = (parameters: Record<string, any>, totalCount: number) => {
    getFilteredPsychicsIds({ ...parameters, PageSize: totalCount || 300, PageIndex: 0 })
      .then((res) => setFilteredExtIds(res.ids))
      .catch((error) => Logger.error('Something went wrong while requesting statuses', error));
  };

  return [
    filteredExtIds,
    getFilteredExtIds,
  ] as [Array<number>, (parameters: Record<string, any>, total: number) => void];
};

export const useAppropriatePsychicsStatuses = (
  list: Array<number>,
  setPsychics: Dispatch<SetStateAction<RightPsychic[]>>,
  condition: boolean,
) => {
  usePsychicStatusesWithUnsubscribe(list, setPsychics, condition);
  usePsychicStatuses([...list], setPsychics, !condition);
};

export const useLoadFiltersOptions = (
  conditionToLoad: boolean = false,
  popularFilters?: PopularFilters,
) => {
  const [options, setOptions] = useState<FilterToolbarOptions>({ category: [], price: [] });
  const [isLoading, setLoadingState] = useState<boolean>(false);
  const wasRequestSent = useRef<boolean | null>(null);

  useEffect(() => {
    if (wasRequestSent.current || !conditionToLoad) {
      return;
    }

    (async () => {
      try {
        setLoadingState(true);
        const {
          categoryOptions,
          priceOptions,
        } = await getPsychicFilterOptions();

        const price = priceOptions.map((price: Record<string, any>) => ({
          ...price,
          psyGroup: price.psy_group,
        }));

        const filtersByCategoriesRaw = (categoryOptions || []).reduce((acc, item) => {
          if (!acc[item.majorCategoryID]) {
            const descriptionMap = {
              Skill: 'Ability',
              Subject: 'Subject',
              Tool: 'Tool',
            };

            const richTextMap = {
              Skill: 'Ability',
              Subject: 'Topics',
              Tool: 'Tools',
            };

            const majorCategoryDescription = descriptionMap[item.majorCategoryDescription]
              || item.majorCategoryDescription;
            const richText = richTextMap[item.majorCategoryDescription]
              || item.majorCategoryDescription;

            acc[item.majorCategoryID] = { majorCategoryDescription, richText, descriptions: [] };
          }

          acc[item.majorCategoryID].descriptions.push(item.description);

          return acc;
        }, {});

        const majorCategoryIdDisplaySequence = [4, 3, 2, 1];
        const category = majorCategoryIdDisplaySequence
          .map((seq) => filtersByCategoriesRaw[seq])
          .filter((category) => category !== undefined);

        if (popularFilters) {
          category.push(popularFilters);
        }

        setOptions({ price, category });
      } finally {
        setLoadingState(false);
      }
    })();
  }, [conditionToLoad, wasRequestSent]);

  return { options, isLoading };
};
